import { FC } from 'react';
import * as React from 'react';
import Markdown from 'react-markdown';
import tw from 'twin.macro';
import 'styled-components/macro';

import { ChallengeStatus } from 'types/graphql';

import { FillButton, GhostButton, TextButton } from '../../atoms/button/Button';
import { Icon } from '../../atoms/icon/Icon';
import { Card } from './Card';

interface Challenge {
  id: string;
  slug: string;
  teaser?: string;
  title: string;
  status: ChallengeStatus;
}

export interface ChallengeCardProps {
  buttonLabel?: string;
  buttonLoading?: boolean;
  challenge: Challenge;
  isAuthor?: boolean;
  onButtonClick?: () => void;
  onDuplicate?: (id: string) => void;
  onEdit?: () => void; // TODO: make required when isAuthor is true
}

export const ChallengeCard: FC<ChallengeCardProps> = ({
  buttonLabel,
  buttonLoading,
  challenge: { id, status, teaser, title, slug },
  isAuthor,
  onButtonClick,
  onDuplicate,
  onEdit,
  ...props
}) => {
  const icon = () => {
    const iconComponent: Record<string, React.ReactNode> = {
      locked: <Icon name="padlock-locked" css={isAuthor ? tw`text-error` : tw`text-gray-50`} />,
      failed: <Icon name="padlock-locked" tw="text-error" />,
      completed: <Icon name="circle-check" tw="text-success" />,
    };
    return iconComponent[status] || <Icon name="padlock-unlocked" tw="text-success" />;
  };

  const button = () => {
    if (!buttonLabel) return null;

    const buttonComponent: Record<string, React.ReactNode> = {
      'Skip to': (
        <GhostButton onClick={() => onButtonClick && onButtonClick()} loading={buttonLoading}>
          <span data-testid={`skip-to-challenge-${slug}`}>{buttonLabel}</span>
        </GhostButton>
      ),
      View: (
        <GhostButton onClick={() => onButtonClick && onButtonClick()} loading={buttonLoading}>
          {buttonLabel}
        </GhostButton>
      ),
      Maintenance: <GhostButton disabled>{buttonLabel}</GhostButton>,
    };
    return (
      buttonComponent[buttonLabel] || (
        <FillButton onClick={() => onButtonClick && onButtonClick()} loading={buttonLoading}>
          {buttonLabel}
        </FillButton>
      )
    );
  };

  return (
    <Card {...props} tw="p-3">
      <div tw="flex flex-col sm:flex-row lg:items-center">
        <div tw="flex items-center mb-3 sm:mb-0 mr-3 text-2xl">{icon()}</div>
        <div tw="flex-grow pr-4">
          <h4 tw="display-4 mb-1">{title}</h4>
          {teaser && <Markdown>{teaser}</Markdown>}
        </div>
        <div tw="lg:text-right mt-3 sm:mt-0 lg:mt-0 flex flex-row-reverse sm:flex-row justify-between sm:justify-end items-center">
          {isAuthor && onDuplicate && (
            <TextButton tw="mr-2" onClick={() => onDuplicate(id)}>
              Duplicate
            </TextButton>
          )}
          {isAuthor && onEdit && (
            <TextButton tw="mr-2" onClick={onEdit}>
              Edit
            </TextButton>
          )}
          {buttonLabel && onButtonClick && button()}
        </div>
      </div>
    </Card>
  );
};
